import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import './GlossaryPanel.scss';

interface GlossaryItem {
  id: string;
  title: string;
  definition: string;
}

interface GlossaryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  scrollToItem?: string;
}

const glossaryItems: GlossaryItem[] = [
  {
    id: 'access-role',
    title: 'Access Role',
    definition: 'An access role defines the permissions and capabilities that a user has within a tenant. Roles determine what actions users can perform and what resources they can access.'
  },
  {
    id: 'catalog',
    title: 'Catalog',
    definition: 'A catalog is a collection of products and services that can be deployed. Catalogs are owned by tenants and can be shared with other tenants to enable cross-tenant collaboration.'
  },
  {
    id: 'environment',
    title: 'Environment',
    definition: 'An environment is a deployment target where products can be instantiated. Environments are accessible to tenants based on their permissions and provide the runtime context for deployed products.'
  },
  {
    id: 'product',
    title: 'Product',
    definition: 'A product is a deployable service or application within a catalog. Products contain the necessary configuration and deployment instructions for various environments.'
  },
  {
    id: 'tenant',
    title: 'Tenant',
    definition: 'A tenant is a logical entity that serves as the top hierarchical item in the system. It is associated with users who have designated access roles, contains catalogs, and has the ability to access shared catalogs. A tenant may also have access to environments to deploy products from any catalog it owns or has shared access to.'
  },
  {
    id: 'user',
    title: 'User',
    definition: 'A user is an individual with authenticated access to the system. Users are associated with tenants and have designated access roles that determine their permissions within the platform.'
  },
  {
    id: 'git-configuration',
    title: 'Git Configuration',
    definition: 'A git configuration defines how a tenant connects to and interacts with GitHub repositories. Each configuration specifies repository access permissions, webhook events, and authentication methods through GitHub App installations.'
  },
  {
    id: 'github-app-integration',
    title: 'GitHub App Integration',
    definition: 'A GitHub App integration enables secure, tenant-specific access to GitHub repositories. Tenants install the ServiceCatalog GitHub App on their repositories to grant controlled access for automated operations like deployments and synchronization.'
  }
];

const GlossaryPanel: React.FC<GlossaryPanelProps> = ({ isOpen, onClose, scrollToItem }) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && scrollToItem) {
      const element = document.getElementById(`glossary-${scrollToItem}`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      }
    }
  }, [isOpen, scrollToItem]);

  if (!isOpen) return null;

  return (
    <div className="glossary-panel__overlay">
      <div className="glossary-panel" ref={panelRef}>
        <div className="glossary-panel__header">
          <h2>Glossary</h2>
          <button className="glossary-panel__close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="glossary-panel__content">
          {glossaryItems.map((item) => (
            <div key={item.id} id={`glossary-${item.id}`} className="glossary-panel__item">
              <h3 className="glossary-panel__title">{item.title}</h3>
              <p className="glossary-panel__definition">{item.definition}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GlossaryPanel;